import { useId, type ReactNode } from 'react';
import { CalendarBlank, Clock, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

const weekdayDayMonth = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
const withYear = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/** "2026-10-20" -> "Mon 20 Oct" (or "Mon 20 Oct 2026"). */
export const formatFieldDate = (iso: string, showYear = false) =>
  (showYear ? withYear : weekdayDayMonth).format(new Date(`${iso}T00:00:00.000Z`));

/** A 24-hour `HH:mm` value as "7:30 PM". */
export const formatFieldTime = (value: string) => {
  const [h = '0', m = '0'] = value.split(':');
  const hour = Number(h);
  return `${hour % 12 === 0 ? 12 : hour % 12}:${m.padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
};

interface FieldShellProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  helperText?: ReactNode;
  placeholder: string;
  /** What the value reads as ("Mon 20 Oct"). */
  display: string;
  icon: PhosphorIcon;
  type: 'date' | 'time';
  disabled?: boolean;
  className?: string;
}

/**
 * A grey 44px field that reads like the design ("Mon 20 Oct", "7:30 PM") rather than the browser's dd/mm/yyyy. The
 * native input sits invisibly over it, so the platform picker, keyboard entry and screen readers all still work.
 */
function FieldShell({
  label,
  value,
  onChange,
  error,
  helperText,
  placeholder,
  display,
  icon,
  type,
  disabled = false,
  className,
}: FieldShellProps) {
  const id = useId();
  const message = error ?? helperText;
  return (
    <div className={cx('flex flex-col gap-(--mbm-space-2)', className)}>
      <label htmlFor={id} className="text-label-medium text-text-primary">
        {label}
      </label>
      <div
        className={cx(
          'relative flex h-(--mbm-size-input-filled) items-center gap-(--mbm-space-2) rounded-(--mbm-radius-sm) border bg-surface-tint px-(--mbm-space-3-5)',
          'focus-within:outline-2 focus-within:-outline-offset-2',
          error
            ? 'border-feedback-error focus-within:outline-feedback-error'
            : 'border-transparent focus-within:outline-focus-ring',
          disabled && 'opacity-(--mbm-opacity-disabled)',
        )}
      >
        <span className="text-text-tertiary">
          <Icon icon={icon} size="md" />
        </span>
        <span className={cx('text-body-medium', value ? 'text-text-primary' : 'text-text-muted')}>
          {value ? display : placeholder}
        </span>
        <input
          id={id}
          type={type}
          value={value}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange(e.target.value)}
          onClick={(e) => e.currentTarget.showPicker?.()}
          className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
      </div>
      {message ? (
        <p
          role={error ? 'alert' : undefined}
          className={cx('text-body-small', error ? 'text-feedback-error' : 'text-text-tertiary')}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
}

export interface DateFieldProps {
  label: string;
  /** `YYYY-MM-DD`, or '' when empty. */
  value: string;
  onChange: (value: string) => void;
  error?: string;
  helperText?: ReactNode;
  placeholder?: string;
  /** "Thu 23 Oct 2026" instead of "Thu 23 Oct". */
  showYear?: boolean;
  disabled?: boolean;
  className?: string;
}

export function DateField({ placeholder = 'Pick a date', showYear = false, ...props }: DateFieldProps) {
  return (
    <FieldShell
      {...props}
      type="date"
      icon={CalendarBlank}
      placeholder={placeholder}
      display={props.value ? formatFieldDate(props.value, showYear) : ''}
    />
  );
}

/** Like DateField, but the value is a 24-hour `HH:mm` (or '' when empty). */
export type TimeFieldProps = Omit<DateFieldProps, 'showYear'>;

export function TimeField({ placeholder = 'Pick a time', ...props }: TimeFieldProps) {
  return (
    <FieldShell
      {...props}
      type="time"
      icon={Clock}
      placeholder={placeholder}
      display={props.value ? formatFieldTime(props.value) : ''}
    />
  );
}
